/**
 * Should the counterparty invite carry the document with it?
 *
 * `sendDealToReceiver` only transitions the document when a `documentId`
 * reaches the server: the mutation does `if document.present?` and then runs
 * `request_document_review` (status → reviewing, possession + receiver team
 * moved to the counterparty) or `share_document`. Without the id the deal
 * invite is sent and the document silently stays a DRAFT owned by the creator.
 *
 * This lived inline in the submit handler and its editor-readiness input was
 * `window.editor?.state === 'ready'` — a CKEditor-only global that is
 * permanently undefined under the DOCX-native editor. That made the whole
 * predicate false for every app-authored document, which is the bug this
 * helper exists to keep from coming back.
 */
export const shouldSendWithDocumentId = ({
  shouldAccessBeGranted,
  documentId,
  isDocumentUploaded,
  isEditorReady,
  sendWithDocumentId,
}: {
  /** Grant-access flow: the document goes along regardless of editor state. */
  shouldAccessBeGranted?: boolean;
  documentId?: string | null;
  /** Imported PDF/DOCX — no editor is ever mounted for it. */
  isDocumentUploaded: boolean;
  /** App-authored document whose editor has finished loading (any engine). */
  isEditorReady: boolean;
  /** Call-site opt-in; false for the pure "invite to deal" entry points. */
  sendWithDocumentId: boolean;
}): boolean => {
  if (shouldAccessBeGranted) return true;
  if (!documentId || !sendWithDocumentId) return false;

  return isDocumentUploaded || isEditorReady;
};
